import type { SlotInputNode } from 'types/packages-api/slot-valuation-inputs';
import { TearsheetLink } from './tearsheet-link';
import { missingRecordNote } from './utils';
import type { VolumeSourceKind } from './source-blocks';
import styles from './slot-tearsheet.module.scss';

/**
/**
 * ── THE DECLINE, NAMED AND LINKED AT THE TOP OF ITS OWN BLOCK ────────────────────────────
 *
 * The actual complaint, verbatim: "we don't have a link anywhere on the PDP decline…
 * when you look at a type curve, I think we link the type curve at the top." This is the
 * mirror of {@link TypeCurveHeader}, reading the SAME node — `slot_volume_source` — and the
 * same three serialized states.
 *
 * ── WHY IT MATTERS MORE THAN THE STATS ───────────────────────────────────────────────────
 *
 * Measured 22 Sep 2026: 408 of 424 non-paceable slots carry a volume source, and only 3 have
 * decline parameters. The stats are empty on 96% of the wells; the name is present on 96%. A
 * section without this row carried nothing at all on nearly every slot.
 *
 * ── TWO SHAPES, LABELLED DIFFERENTLY, NEITHER INVENTED ───────────────────────────────────
 *
 * A Legacy source's value IS a display name —
 * "3002540632_DNP_LETO_20250903_Madera 092025 Update_PDP_IHS". A DCA source's value is a bare
 * forecast id — 25472 — and the forecast record carries no name to use instead (checked: its
 * `notes` is a UAT artifact note). So the id is shown AS an id, said out loud in the label,
 * rather than dressed up as a name or derived from the Legacy convention.
 *
 * @param {object} props - The block's pdp-decline kind and the full node list.
 * @return {(JSX.Element|null)} The named decline, or null when this slot has no source.
 */
export function PdpDeclineHeader({
  pdpDecline,
  allNodes,
}: {
  pdpDecline: VolumeSourceKind | null;
  allNodes: SlotInputNode[];
}) {
  // ONE KIND NOW DRIVES BOTH HEADERS, which is what makes them mutually exclusive by
  // construction rather than by the block they used to sit in: this renders for the two
  // decline outcomes, TypeCurveHeader for the curve one, and neither for unknown_source.
  if (pdpDecline !== 'fitted_decline' && pdpDecline !== 'named_source') {
    return null;
  }
  const node = allNodes.find(
    (candidate) => candidate.key === 'slot_volume_source'
  );
  // ── ⚠️ THE RUN'S SOURCE, NOT THE PENDING PICK'S ─────────────────────────────────────
  //
  // This read `value`, the OVERLAID one. Under a pending volume-source change that moves a
  // PDP well onto a type curve, the header read "PDP decline: <the new curve>" — the new
  // curve's NAME under the old status's LABEL, because `pdpDecline` comes from
  // `forecastStatus`, which is the run's `'pdp'`. It sat directly above the block's own
  // note saying the change is not in these numbers yet, contradicting it.
  //
  // The block's contract is that note's last sentence: "The parameters below are the ones
  // this run used." So the header names what the run used. `runValue` is served only where
  // the read-time overlay displaced the node, so the fallback to `value` is the ordinary
  // path — with no override nothing is displaced and the two are the same string.
  const entry = node?.values?.[0];
  const value = entry?.runValue ?? entry?.value;
  if (value === null || value === undefined || value === '') {
    return null;
  }
  // A forecast is named by its business_id when the server served one (valueLabel); a number
  // with no name is a forecast id and says so; a string is the source's own name. The label
  // names the run's source only, so it is not shown for a pending pick.
  const runName = entry?.runValue === undefined ? node?.valueLabel : null;
  const shown = runName ?? String(value);
  const isIdentifier = typeof value === 'number' && !runName;
  const label = isIdentifier ? 'PDP decline id: ' : 'PDP decline: ';
  return (
    <p className={styles.blockCaption}>
      <span className={styles.contextLabel}>{label}</span>
      {node?.valueHref ? (
        <TearsheetLink href={node.valueHref}>{shown}</TearsheetLink>
      ) : (
        <strong>{shown}</strong>
      )}
    </p>
  );
}

/**
 * ── THE TYPE CURVE, NAMED AND LINKED AT THE TOP OF ITS OWN BLOCK (UAT 4, 9 Sep) ──────────
 *
 * A user had to discover which curve generated the values by scrolling to Volumes and
 * reading the Volume Source row. The curve is the subject of this block, so it says so at
 * the top.
 *
 * ── PLACEMENT ONLY. THE RESOLVER DOES NOT MOVE. ──────────────────────────────────────────
 *
 * The name and the href are already served, on the `slot_volume_source` node —
 * VALUE_HREF_RESOLVERS maps it to `type_curve_href` (node_binder.rb:108). That home is a
 * deliberate decision with a three-outcome design behind it (link /
 * record-missing / no-lookup), so this reads that node from `allNodes` and renders it here
 * as well. Nothing about where it resolves changes, and the Volume Source row keeps its own
 * copy — the same fact in the two places a reader looks for it.
 *
 * ── THE WELL-FORECAST CASE IS THE ONE THAT MATTERS ───────────────────────────────────────
 *
 * A PDP slot's volume source is not a type curve. Its id looks like one
 * (`..._PDP_IHS`) and matches no `type_curve_header_details` row, so the backend returns
 * `valueRecordMissing` rather than an href — and a header that assumed a link would be wrong
 * on every PDP slot, which is the category where the missing link was noticed.
 *
 * So this renders NOTHING unless the block's own `volumeSourceKind` says `type_curve`. That
 * signal is measured from whether any type-curve node actually resolved, not from the slot's
 * category, so a DUC with a broken lookup behaves like the PDP rather than being called a
 * working link. When it renders nothing, the existing VOLUME_SOURCE_COPY caption below
 * already states what the volumes did come from.
 *
 * @param {object} props - The block's volume-source kind and the full node list.
 * @return {(JSX.Element|null)} The named curve, or null when this slot has none.
 */
export function TypeCurveHeader({
  volumeSource,
  allNodes,
}: {
  volumeSource: VolumeSourceKind | null;
  allNodes: SlotInputNode[];
}) {
  if (volumeSource !== 'type_curve') {
    return null;
  }
  const node = allNodes.find(
    (candidate) => candidate.key === 'slot_volume_source'
  );
  const name = node?.values?.[0]?.value;
  if (typeof name !== 'string' || !name) {
    return null;
  }
  // Same three outcomes as the Volume Source row, read from the same fields.
  const missing = missingRecordNote(node?.valueRecordMissing);
  return (
    <p className={styles.blockCaption}>
      <span className={styles.contextLabel}>Type curve: </span>
      {node?.valueHref ? (
        <TearsheetLink href={node.valueHref}>{name}</TearsheetLink>
      ) : (
        <strong>{name}</strong>
      )}
      {missing && <span className={styles.missingRecord}>{missing}</span>}
    </p>
  );
}
